import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { celebrateOnce } from "@/lib/celebrate";
import HeroDecor from "@/components/HeroDecor";
import {
  Puzzle, Repeat, Layers, Workflow,
  GraduationCap, BookOpen, Laptop, BarChart3, Trophy,
  ArrowRight, ArrowDown, Sparkles,
  Instagram, Youtube, Mail, Phone,
} from "lucide-react";

const skills = [
  { name: "Decomposition", icon: Puzzle, bg: "#FBCFE8", ring: "#F9A8D4", desc: "Breaking problems into smaller parts" },
  { name: "Pattern Recognition", icon: Repeat, bg: "#FDE68A", ring: "#FCD34D", desc: "Spotting rules and repetition" },
  { name: "Abstraction", icon: Layers, bg: "#E9D5FF", ring: "#D8B4FE", desc: "Focusing on what really matters" },
  { name: "Algorithmic Thinking", icon: Workflow, bg: "#BAE6FD", ring: "#7DD3FC", desc: "Building step-by-step solutions" },
];

const steps = [
  { icon: GraduationCap, bg: "#E9D5FF", label: "Choose your class (3 to 8)" },
  { icon: BookOpen, bg: "#FDE68A", label: "Select chapter" },
  { icon: Laptop, bg: "#DDD6FE", label: "Solve CT-based questions" },
  { icon: BarChart3, bg: "#BAE6FD", label: "Get your result instantly" },
  { icon: Trophy, bg: "#BBF7D0", label: "See your CT skill profile" },
];

const ctDefinition = [
  { n: 1, text: "A systematic approach." },
  { n: 2, text: "To solve." },
  { n: 3, text: "Any problem." },
];

// Social links.
const socials = [
  { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/gdiot_?igsi=MWN3d296OHRsNnJkZA==", color: "#E1306C" },
  { name: "YouTube", icon: Youtube, href: "https://youtube.com/@geniusidiottt?si=G_MD2c7Yrv4Ojk0d", color: "#FF0000" },
];

function SectionTitle({ children }) {
  return (
    <div className="flex items-center justify-center gap-3">
      <Sparkles className="h-4 w-4 text-violet-300" />
      <h2 className="font-head font-bold text-xl sm:text-2xl tracking-wide text-slate-900 uppercase">{children}</h2>
      <Sparkles className="h-4 w-4 text-violet-300" />
    </div>
  );
}

export default function Landing() {
  useEffect(() => {
    const t = setTimeout(() => celebrateOnce("ct_landing_celebrated"), 350);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9FF] flex flex-col">
      {/* HEADER */}
      <header className="border-b border-violet-100 bg-white/70 backdrop-blur-sm sticky top-0 z-30">
        <div className="w-full px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/gdiot-logo-wide.png" alt="GDIOT" className="h-11 sm:h-12 w-auto object-contain" data-testid="app-logo" />
            <span className="hidden sm:block h-7 w-px bg-slate-200" />
            <span className="font-head font-semibold text-base sm:text-lg text-slate-900">CT Skill Assessment</span>
          </div>
          <Link to="/staff-login">
            <Button variant="outline" size="sm" className="rounded-full border-primary/30 text-primary hover:bg-accent gap-1.5" data-testid="header-staff-login">
              <GraduationCap className="h-4 w-4" /> Staff Login
            </Button>
          </Link>
        </div>
      </header>

      {/* HERO — founder + cards scale as ONE centered composition across all widths */}
      <section className="relative flex flex-col justify-center min-h-[calc(100dvh-5rem)] py-4">
        <HeroDecor />
        <div className="relative z-10 shrink-0 max-w-5xl mx-auto w-full px-6 sm:px-8 text-center">
          <h1 className="font-hand font-bold leading-[0.92] text-slate-900 text-5xl sm:text-6xl lg:text-7xl max-w-4xl mx-auto">
            Every genius was once called an
            <span className="text-primary relative inline-block ml-2 sm:ml-3">
              idiot.
              <svg className="absolute left-0 -bottom-2 w-full h-3.5 text-amber-400" viewBox="0 0 200 16" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M4 11C55 4 150 3 196 8" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
              </svg>
              <span className="absolute -top-4 -right-7 text-amber-400 text-3xl">✦</span>
            </span>
          </h1>
          <p className="mt-3 text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            At GDIOT (Genius Idiot), we believe great thinking starts with curiosity, questions and the courage to think differently.
          </p>
          <p className="mt-2 font-hand text-2xl sm:text-3xl text-primary font-bold">So… how do you think?</p>
          <div className="flex justify-center mt-0.5"><ArrowDown className="h-5 w-5 text-violet-400 animate-bounce" /></div>
        </div>

        {/* Founder + Student/Teacher cards = one composition, centered, fluid width */}
        <div className="relative z-10 w-full flex justify-center mt-3 sm:mt-4">
          <div className="relative mx-auto select-none w-[clamp(320px,92vw,1550px)]" data-testid="hero-image-wrap">
            <picture>
              <source srcSet="/founder.webp?v=4" type="image/webp" />
              <img src="/founder.png?v=4" alt="Founder — choose Student or Teacher" fetchPriority="high" decoding="async" width="1536" height="672" className="block w-full h-auto object-contain" data-testid="hero-image" />
            </picture>
            <Link to="/student-login" aria-label="I'm a Student — take a test" title="I'm a Student"
              data-testid="hero-student-zone"
              className="absolute rounded-full ring-2 ring-transparent hover:ring-4 hover:ring-primary/60 focus:ring-4 focus:ring-primary/60 focus:outline-none transition-all"
              style={{ left: "25.4%", top: "54.8%", width: "8%", height: "13%" }} />
            <Link to="/staff-login" aria-label="Teacher / Admin login" title="Teacher / Admin"
              data-testid="hero-teacher-zone"
              className="absolute rounded-full ring-2 ring-transparent hover:ring-4 hover:ring-amber-400/70 focus:ring-4 focus:ring-amber-400/70 focus:outline-none transition-all"
              style={{ left: "67.3%", top: "56.2%", width: "8%", height: "13%" }} />
          </div>
        </div>
      </section>

      {/* WHAT IS COMPUTATIONAL THINKING */}
      <section className="max-w-6xl mx-auto w-full px-6 sm:px-8 mt-4 sm:mt-8">
        <div className="bg-white rounded-[2rem] border border-violet-100 shadow-[0_10px_40px_-20px_rgba(124,58,237,0.35)] p-8 sm:p-12">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="flex items-center gap-6">
              {/* friendly doodle mascot */}
              <svg viewBox="0 0 130 130" className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M35 78c0-24 12-34 30-34s30 10 30 34v6c0 12-8 18-20 18H55c-12 0-20-6-20-18z" fill="#F5F3FF" />
                <circle cx="53" cy="70" r="4.5" fill="currentColor" stroke="none" />
                <circle cx="77" cy="70" r="4.5" fill="currentColor" stroke="none" />
                <path d="M56 86c5 4 13 4 18 0" />
                <path d="M65 44V30" />
                <circle cx="65" cy="22" r="8" fill="#FDE68A" stroke="#F59E0B" />
                <path d="M61 30h8" stroke="#F59E0B" />
                <path d="M35 66l-9-4M95 66l9-4" />
              </svg>
              <h2 className="font-head font-bold text-3xl sm:text-4xl leading-tight text-slate-900">
                What is<br /><span className="text-primary">Computational<br />Thinking?</span>
                <span className="block h-1.5 w-28 bg-amber-300 rounded-full mt-3" />
              </h2>
            </div>

            <div className="md:border-l md:border-violet-100 md:pl-10">
              {ctDefinition.map((s, i) => (
                <div key={s.n}>
                  <div className="flex items-center gap-4">
                    <span className="h-9 w-9 shrink-0 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">{s.n}</span>
                    <span className="text-lg sm:text-xl font-medium text-slate-800">{s.text}</span>
                  </div>
                  {i < ctDefinition.length - 1 && (
                    <div className="pl-[14px] my-2"><ArrowDown className="h-5 w-5 text-violet-300" /></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUR CT SKILLS */}
      <section className="max-w-6xl mx-auto w-full px-6 sm:px-8 pt-16 sm:pt-20">
        <SectionTitle>The Four CT Skills</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {skills.map((s) => (
            <div key={s.name} className="bg-white rounded-3xl border border-violet-100 shadow-sm hover:shadow-md transition-shadow p-7 text-center flex flex-col items-center">
              <div className="h-20 w-20 rounded-full flex items-center justify-center" style={{ background: s.bg, boxShadow: `0 0 0 6px ${s.ring}33` }}>
                <s.icon className="h-9 w-9 text-slate-800" />
              </div>
              <p className="font-head font-semibold text-lg text-slate-900 mt-5">{s.name}</p>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-slate-500 mt-8 leading-relaxed max-w-3xl mx-auto flex items-center justify-center gap-2 flex-wrap">
          <Sparkles className="h-4 w-4 text-violet-300" />
          Each competency is scored per test with a clear performance band —{" "}
          <span className="text-primary font-semibold">Strong, Developing or Needs Support.</span>
        </p>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto w-full px-6 sm:px-8 pt-16 sm:pt-20 pb-16">
        <SectionTitle>How It Works</SectionTitle>
        <div className="mt-10 flex flex-col md:flex-row md:items-start md:justify-between gap-6 md:gap-2">
          {steps.map((st, i) => (
            <React.Fragment key={st.label}>
              <div className="flex md:flex-col items-center md:text-center gap-4 md:gap-3 md:flex-1">
                <div className="h-16 w-16 shrink-0 rounded-full flex items-center justify-center" style={{ background: st.bg }}>
                  <st.icon className="h-7 w-7 text-slate-800" />
                </div>
                <p className="text-sm font-medium text-slate-700 md:max-w-[9rem]">
                  <span className="text-primary font-bold">{i + 1}.</span> {st.label}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div className="flex items-center justify-center md:pt-5 text-violet-300">
                  <ArrowRight className="hidden md:block h-6 w-6" />
                  <ArrowDown className="md:hidden h-5 w-5" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-violet-100 bg-white mt-auto" data-testid="site-footer">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 lg:px-12 py-12">
          <div className="grid gap-10 md:grid-cols-3 items-start text-center md:text-left">
            {/* Brand */}
            <div className="flex flex-col items-center md:items-start gap-3">
              <img src="/gdiot-logo-wide.png" alt="GDIOT" className="h-11 w-auto object-contain" />
              <div>
                <p className="font-head font-semibold text-slate-900">GDIOT · Genius Idiot</p>
                <p className="text-sm text-slate-500 mt-1">© 2026 All rights reserved.</p>
              </div>
            </div>

            {/* Socials — centered & animated */}
            <div className="flex flex-col items-center gap-4">
              <p className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-400">Follow us</p>
              <div className="flex items-center justify-center gap-6" data-testid="footer-socials">
                {socials.map((s) => (
                  <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer"
                    aria-label={s.name} title={s.name} data-testid={`social-${s.name.toLowerCase()}`}
                    className="h-12 w-12 rounded-full flex items-center justify-center text-white shadow-md hover:shadow-lg transition-transform duration-200 hover:-translate-y-1 hover:scale-110"
                    style={{ background: s.color }}>
                    <s.icon className="h-6 w-6" />
                  </a>
                ))}
              </div>
              <p className="text-xs text-slate-400">Tap to connect with us</p>
            </div>

            {/* Contact */}
            <div className="flex flex-col items-center md:items-end gap-3">
              <p className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-400">Get in touch</p>
              <a href="mailto:support@gdiot.in" className="flex items-center gap-2 text-slate-700 hover:text-primary transition-colors" data-testid="footer-email">
                <span className="h-8 w-8 rounded-full bg-accent flex items-center justify-center"><Mail className="h-4 w-4 text-primary" /></span>
                support@gdiot.in
              </a>
              <a href="tel:+919146149120" className="flex items-center gap-2 text-slate-700 hover:text-primary transition-colors" data-testid="footer-phone">
                <span className="h-8 w-8 rounded-full bg-accent flex items-center justify-center"><Phone className="h-4 w-4 text-primary" /></span>
                +91 91461 49120
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
